import { engineComponent, StringUtils } from "neo-common";
import { HtmlElement } from "./HtmlElement";
import { HtmlContainerStyle } from "../Styles/HtmlContainerStyle";
import { StyleManager } from "../../Components/Managers/StyleManager";
import { HtmlManager } from "../../Components/Managers/HtmlManager";
import { HtmlController } from "../HtmlController";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { PlayList } from "../../Components/Sounds/PlayList";

/**
 * The HtmlContainerElement class is class which creates holder for other html elements.
 * Can be defined also by Style with class HtmlContainerStyle.
 * Example 1:
 *   <HtmlContainerElement Class="HtmlContainerElement">
 *       <Child1 />
 *       <Child1 />
 *       ...
 *   </HtmlContainerElement>
 *
 * Example 2:
 *   <HtmlContainerElement Class="HtmlContainerElement" Style="HtmlContainerStyle" />
 *
 * @class HtmlContainerElement
 * @extends HtmlElement
 */
@engineComponent(ComponentTypes.HtmlElement)
export class HtmlContainerElement extends HtmlElement {
    static Over: string = "mouseover";
    static Out: string = "mouseout";
    static TouchStart: string = "touchstart";
    static TouchEnd: string = "touchend";
    static CustomClick: string = "click";

    private _overId: string;
    private _clickId: string;

    childs: Object;
    style: HtmlContainerStyle;

    childrenHolderClass: string = "";
    childrenClass: string = "";
    recalculateHeight: boolean = false;

    constructor() {
        super();
    }

    /**
     * Get attributes from xml and set up supported properties.
     * @method create
     * @param node is Element from xml
     * @return void
     */
    create(node: Element): void {
        super.create(node);

        if (node.hasAttribute("id")) {
            HtmlController.ContainerElementsById[node.getAttribute("id")] = this;
        }

        /*
         * Style can be define for better usage of html container elements.
         * For example when you want to use same html element with same names on more than one places.
         * Style for container element has to be instance of HtmlContainerStyle
         * @property Style
         * @type string
         * @default null
         */
        if (node.hasAttribute("Style")) {
            // get style from loaded resources
            this.style = <HtmlContainerStyle>StyleManager.HtmlStyles[node.getAttribute("Style")];
            if (this.style != null) {
                // parse childs from style
                this.childs = HtmlManager.parseElements(this.style.node, <HtmlElement>this);
            } else {
                if (DEBUG) {
                    this.logError("ERROR no style with name " + node.getAttribute("Style"));
                }
            }
        } else {
            // parse childs from node
            this.childs = HtmlManager.parseElements(node, <HtmlElement>this);
        }

        if (this.childs == null) {
            if (DEBUG) {
                this.log("WARNING html container element with name " + node.nodeName + " has no children");
            }
        }

        /*
         * Attribute OnOver define element which will be react to over event handled from this element.
         * @property OnOver
         * @type string
         * @default null
         */
        if (node.hasAttribute("OnOver")) {
            if (node.hasAttribute("OnOverAction")) {
                var actionEnabled: boolean = HtmlController.Configurations[node.getAttribute("OnOverAction")];
                if (actionEnabled) {
                    this._overId = node.getAttribute("OnOver");
                }
            } else {
                this._overId = node.getAttribute("OnOver");
            }
        }

        if (node.hasAttribute("ChildrenHolderClass")) {
            this.childrenHolderClass = node.getAttribute("ChildrenHolderClass");
        }
        if (node.hasAttribute("ChildrenClass")) {
            this.childrenClass = node.getAttribute("ChildrenClass");
        }

        if (node.hasAttribute("RecalculateHeight")) {
            this.recalculateHeight = StringUtils.parseBoolean(node.getAttribute("RecalculateHeight"));
        }

        if (node.hasAttribute("OnClick")) {
            if (node.hasAttribute("OnClickAction")) {
                this._clickId = node.getAttribute("OnClick");
                this.instance.addEventListener(HtmlContainerElement.CustomClick, this.onCustomClick.bind(this));
            }
        }

        if (this._overId != null) {
            this.instance.addEventListener(HtmlContainerElement.Over, this.onOver.bind(this));
            this.instance.addEventListener(HtmlContainerElement.Out, this.onOut.bind(this));
        }

        if (node.hasAttribute("ActionsHolder")) {
            HtmlController.registerActionsHoldersElement(node.getAttribute("ActionsHolder"), this);
        }

        /*
         * Test is html data attribute for automated testing support.
         * @property Test
         * @type string
         * @default null
         */
        if (node.hasAttribute("Test")) {
            this.instance.dataset["test"] = node.getAttribute("Test");
        }

        /*
         * Attribute UIDispatcher define element which will be dispatch UI action from this element.
         * @property UIDispatcher
         * @type string
         * @default null
         */
        if (node.hasAttribute("UIDispatcher")) {
            HtmlController.registerUiDispatchElement(node.getAttribute("UIDispatcher"), this);
        }

        /*
         * Attribute UIActionDispatcher define element which will be dispatch UI action from this element.
         * @property UIActionDispatcher
         * @type string
         * @default null
         */
        if (node.hasAttribute("UIActionDispatcher")) {
            HtmlController.registerUiActionDispatchElement(node.getAttribute("UIActionDispatcher"), this);
        }

        /**
         * Attribute TouchScroll define element scrollable by touchmove.
         * @type {boolean}
         */
        let touchScroll: boolean = false;
        if (node.hasAttribute("TouchScroll")) {
            touchScroll = StringUtils.parseBoolean(node.getAttribute("TouchScroll"));
        }

        if (touchScroll) {
            this.enableScrollOnTouchmove();
        }
    }

    /**
     *
     */
    private enableScrollOnTouchmove(): void {
        var allowScrollUp = true;
        var allowScrollDown = true;
        var lastY = 0;
        let self = this;

        /**
         * On Touchstart event remember init values.
         */
        this.instance.addEventListener(
            "touchstart",
            function (event) {
                // Decide enabled direction
                allowScrollUp = this.scrollTop > 0;
                allowScrollDown = this.scrollTop < this.scrollHeight - this.clientHeight;

                // Remember where the touch started
                lastY = event.touches[0].pageY;
            },
            true,
        );

        /**
         * On Touchmove event decide if scroll if possible up or down.
         */
        this.instance.addEventListener(
            "touchmove",
            function (event) {
                // Action only for one finger touch
                if (event.touches.length === 1) {
                    // Trying move by touch up or down?
                    let up = event.touches[0].pageY > lastY;
                    let down = !up;
                    lastY = event.touches[0].pageY;

                    // Trying to start past scroller bounds
                    if ((up && allowScrollUp) || (down && allowScrollDown)) {
                        // Stop this event from propagating, lest another object cancel it.
                        event.stopPropagation();
                    } else {
                        // Cancel this event
                        event.preventDefault();
                    }
                }
            },
            true,
        );
    }

    /**
     * Handler for on over event.
     * @method onOver
     * @return void
     */
    private onOver(): void {
        if (this._overId != null) {
            var element: HTMLElement = document.getElementById(this._overId);
            if (element != null) {
                element.classList.remove(HtmlController.DisabledClass);
            }
        }
    }

    /**
     * Handler for on out event.
     * @method onOut
     * @return void
     */
    private onOut(): void {
        if (this._overId != null) {
            var element: HTMLElement = document.getElementById(this._overId);
            if (element != null) {
                element.classList.add(HtmlController.DisabledClass);
            }
        }
    }

    private onCustomClick(): void {
        if (this._clickId != null && HtmlController.ContainerElementsById[this._clickId] != null) {
            PlayList.play("SND_Click");
            var element: HtmlContainerElement = HtmlController.ContainerElementsById[this._clickId];
            if (element != null && element.instance != null) {
                if (element.instance.classList.contains(HtmlController.DisabledClass)) {
                    HtmlController.dispatchUiAction("");
                    if (element.recalculateHeight) {
                        element.recalculateChildrenHeight(element);
                    }
                    element.reset();
                    element.instance.classList.remove(HtmlController.DisabledClass);
                } else {
                    if (element.recalculateHeight) {
                        element.instance.removeAttribute("style");
                    }
                    element.instance.classList.add(HtmlController.DisabledClass);
                    HtmlController.dispatchUiAction("");
                }
                if (document.getElementById("autoPlayDialogTop") != null) {
                    document.getElementById("autoPlayDialogTop").scrollTop = 0;
                }
            }
        }
    }

    recalculateChildrenHeight(element: HtmlContainerElement): void {
        if (element.instance != null && element.childrenHolderClass != "" && element.childrenClass != "") {
            let holders: NodeListOf<Element> = element.instance.getElementsByClassName(element.childrenHolderClass);
            if (holders[0] != null) {
                let paddingTop: number = parseInt(
                    window.getComputedStyle(holders[0], null).getPropertyValue("padding-top"),
                );
                let paddingBottom: number = parseInt(
                    window.getComputedStyle(holders[0], null).getPropertyValue("padding-bottom"),
                );
                let holdersChildren: NodeListOf<Element> = holders[0].getElementsByClassName(element.childrenClass);
                if (holdersChildren != null && holdersChildren.length > 0) {
                    let height: number = paddingTop + paddingBottom;
                    for (let i: number = 0; i < holdersChildren.length; i++) {
                        let itemHeight: number = parseInt(
                            window.getComputedStyle(holdersChildren[i], null).getPropertyValue("height"),
                        );
                        height += itemHeight;
                    }
                    element.instance.style.height = height + "px";
                }
            }
        }
    }

    changeState(value: boolean): void {
        if (this.instance != null) {
            if (value) {
                this.instance.classList.remove(HtmlController.DisabledClass);
            } else {
                this.instance.classList.add(HtmlController.DisabledClass);
                if (this._clickId != null && HtmlController.ContainerElementsById[this._clickId] != null) {
                    var element: HtmlContainerElement = HtmlController.ContainerElementsById[this._clickId];
                    if (element != null && element.instance != null) {
                        if (!element.instance.classList.contains(HtmlController.DisabledClass)) {
                            if (element.recalculateHeight) {
                                element.instance.removeAttribute("style");
                            }
                            element.instance.classList.add(HtmlController.DisabledClass);
                        }
                    }
                }
            }
        } else {
            if (DEBUG) {
                this.log("ERROR no instance");
            }
        }
    }

    reset(): void {}
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/HTMLComponents/Elements/HtmlContainerElement.ts
 **/